import { keyBy } from "lodash";
import moment from "moment";
import FormTextboxIcon from "vue-material-design-icons/FormTextbox.vue";
import NoteTextIcon from "vue-material-design-icons/NoteText.vue";
import CheckboxMarkedOutlineIcon from "vue-material-design-icons/CheckboxMarkedOutline.vue";
import NumericIcon from "vue-material-design-icons/Numeric.vue";
import CurrencyUsdIcon from "vue-material-design-icons/CurrencyUsd.vue";
import CalendarTextIcon from "vue-material-design-icons/CalendarText.vue";
import FormatListChecksIcon from "vue-material-design-icons/FormatListChecks.vue";
import CloudUploadIcon from "vue-material-design-icons/CloudUpload.vue";
import WindowMaximizeIcon from "vue-material-design-icons/WindowMaximize.vue";
import CodeEqualIcon from "vue-material-design-icons/CodeEqual.vue";
import TableColumnPlusAfterIcon from "vue-material-design-icons/TableColumnPlusAfter.vue";
import TablePlusIcon from "vue-material-design-icons/TablePlus.vue";
import LanguageHtml5Icon from "vue-material-design-icons/LanguageHtml5.vue";
import CalculatorVariantIcon from "vue-material-design-icons/CalculatorVariant.vue";

import GenericValidation from "./widget/GenericValidation.js";
import StringValidation from "./widget/text/Validation.js";
import NumberValidation from "./widget/number/Validation.js";
import SectionValidation from "./widget/section/Validation.js";
import ConditionalValidation from "./widget/conditional/Validation.js";
import ManyValidation from "./widget/repeat/Validation.js";

export const FIELD_TYPES = [
  {
    name: "Input",
    code: "text",
    icon: FormTextboxIcon,
    default_value: "",
    simple: true,
    value: "",
    disabled: false,
    validator: StringValidation,
    params: {
      minlength: 0,
      placeholder: "",
      maxlength: 255,
      title: "",
      type: "text",
    },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Multi-line Text",
    code: "textarea",
    icon: NoteTextIcon,
    default_value: "",
    simple: true,
    value: "",
    validator: StringValidation,

    params: {
      rows: 5,
      layout: "textarea",
      required: false,
      cols: 200,
      title: "",
    },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Yes/No",
    code: "checkbox",
    icon: CheckboxMarkedOutlineIcon,
    default_value: null,
    simple: true,
    value: null,
    validator: GenericValidation,
    params: {
      layout: "buttons",
      yesLabel: "Yes",
      yesColor: "",
      noLabel: "No",
      noColor: "",
      allow_na: false,
      naLabel: "N/A",
      naColor: "",
      yesScore: 1,
      noScore: -1,
      naScore: 0,
    },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Yes/No with Text/Files",
    code: "checkbox_text_files",
    icon: CheckboxMarkedOutlineIcon,
    default_value: { val: null, comment: "", files: [] },
    simple: true,
    value: { val: null, comment: "", files: [] },
    validator: GenericValidation,
    params: {
      layout: "buttons",
      yesLabel: "Yes",
      yesColor: "",
      noLabel: "No",
      noColor: "",
      allow_na: false,
      naLabel: "N/A",
      naColor: "",
      yesScore: 1,
      noScore: -1,
      naScore: 0,
    },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: false,
    container: false,
    non_input: false,
    default_function(default_value) {
      return default_value;
    },
  },
  {
    name: "Number",
    code: "number",
    icon: NumericIcon,
    default_value: 0,
    simple: true,
    value: 0,
    validator: NumberValidation,
    params: { step: 1, max: "", placeholder: null, min: 0 },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Money",
    code: "money",
    icon: CurrencyUsdIcon,
    default_value: 0,
    simple: true,
    value: 0,
    validator: NumberValidation,
    params: { max: "", placeholder: null, min: 0 },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Date And Time",
    code: "datetime",
    icon: CalendarTextIcon,
    default_value: "",
    simple: true,
    value: "",
    disabled: false,
    validator: GenericValidation,
    params: {
      placeholder: null,
      max: "",
      title: "",
      min: "",
      input_date: true,
      input_time: true,
    },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function(default_value) {
      // Check the default value for 'key words' and use them
      if (default_value === "now") {
        return moment().toISOString();
      }

      //TODO Add more options ie now+1...now+7 now-7 this_monday, next_monday etc.

      // return what was in there - assumed a datetime const.
      return default_value;
    },
  },
  {
    name: "Options",
    code: "select",
    icon: FormatListChecksIcon,
    default_value: "",
    simple: true,
    value: "",
    validator: GenericValidation,
    params: { items: [], codeType: "number", data_source: "static", ajax_url: "", send_auth: true },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Multi Select Options",
    code: "multiselect",
    icon: FormatListChecksIcon,
    default_value: "",
    simple: true,
    value: "",
    validator: GenericValidation,
    params: { items: [], codeType: "number", data_source: "static", ajax_url: "", send_auth: true },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "File Upload",
    code: "fileupload",
    icon: CloudUploadIcon,

    simple: true,
    value: [],
    validator: GenericValidation,
    params: { multiple: false, photos: false },
    has_attachment: true,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },

  {
    name: "Section",
    code: "section",
    icon: WindowMaximizeIcon,
    simple: false,
    value: "",
    validator: SectionValidation,
    params: { min: 0, max: 1 },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: false,
    container: true,
    non_input: false,
    default_function: null,
  },
  {
    name: "Conditional",
    code: "conditional",
    icon: CodeEqualIcon,

    simple: false,
    value: "",
    validator: ConditionalValidation,
    params: {},
    has_attachment: false,
    show_label: false,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: true,
    non_input: true,
    default_function: null,
  },
  {
    name: "Repeat",
    code: "repeat",
    icon: TableColumnPlusAfterIcon,

    simple: true,
    value: "",
    validator: ManyValidation,
    params: { min: 0, max: 5 },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: true,
    non_input: false,
    default_function: null,
  },
  {
    name: "Table",
    code: "table",
    icon: TablePlusIcon,

    simple: true,
    value: "",
    validator: ManyValidation,
    params: { min: 0, max: 5 },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: false,
    misc: false,
    container: true,
    non_input: false,
    default_function: null,
  },

  {
    name: "Wizard",
    code: "wizard",
    icon: CodeEqualIcon,

    simple: false,
    value: "",
    validator: GenericValidation,
    params: {
      start_button: "Start",
    },
    has_attachment: false,
    show_label: false,
    system_field: true,
    show: false,
    can_score: false,
    misc: false,
    container: true,
    non_input: true,
    default_function: null,
  },
  {
    name: "WizardStep",
    code: "wizardstep",

    simple: false,
    value: "",
    validator: GenericValidation,
    params: {},
    has_attachment: false,
    show_label: false,
    system_field: true,
    show: false,
    can_score: false,
    misc: false,
    container: true,
    non_input: true,
    default_function: null,
    visible: false,
  },
  {
    name: "Static HTML",
    code: "statichtml",
    icon: LanguageHtml5Icon,
    simple: false,
    value: null,
    validator: function (field) {
      return function (item) {
        return null; // nothing to validate
      };
    },
    params: { html: "" },
    has_attachment: false,
    show_label: false,
    system_field: true,
    show: true,
    can_score: false,
    misc: true,
    container: false,
    non_input: true,
    default_function: null,
  },

  {
    name: "Calculated Value",
    code: "formula",
    icon: CalculatorVariantIcon,
    simple: true,
    value: null,
    validator: GenericValidation,
    params: { expression: "", show: true },
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: true,
    can_score: true,
    misc: true,
    container: false,
    non_input: true,
    default_function: null,
  },

  {
    name: "Address",
    code: "address",

    simple: false,
    value: "",
    validator: GenericValidation,
    params: {},
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: false,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
  {
    name: "Extra Fields",
    code: "extra",

    simple: false,
    value: [],
    validator: GenericValidation,
    params: {},
    has_attachment: false,
    show_label: true,
    system_field: true,
    show: false,
    can_score: false,
    misc: false,
    container: false,
    non_input: false,
    default_function: null,
  },
];

export const FIELD_TYPES_BY_CODE = keyBy(FIELD_TYPES, "code");
